import { LitElement, html } from 'lit';

import { ACTION_LABEL, revisionAction } from '../actions.js';
import { api } from '../api/index.js';
import { confirmAction } from '../components/plane-confirm.js';
import { revisionStatusBadge } from '../components/plane-badge.js';
import { loadState } from '../components/load-state.js';
import { markdown } from '../components/plane-markdown.js';
import { when } from '../components/time.js';
import { OPERATOR_RULES, PERSONA } from '../i18n/labels.js';
import { poll } from '../poll.js';
import { delegatesOf } from '../team.js';
import { formStyles } from '../../styles/shared/forms.js';
import { markdownStyles } from '../../styles/shared/markdown.js';
import { pageStyles } from '../../styles/shared/page.js';
import { revisionPageStyles } from '../../styles/pages/revision-page.js';

/** One revision, read only: its parameters and persona, and Apply or Rollback. */
class RevisionPage extends LitElement {
  static properties = {
    name: { type: String },
    revisionId: { type: String },
    busy: { state: true },
    actionError: { state: true },
  };

  static styles = [
    pageStyles,
    formStyles,
    markdownStyles,
    revisionPageStyles,
  ];

  constructor() {
    super();
    this.busy = false;
    this.actionError = null;
    // the revision never changes; the instance does (desired, applied), so the page keeps polling
    this.poll = poll(this, async () => {
      const [instance, revision] = await Promise.all([api.instance(this.name), api.revision(this.name, this.revisionId)]);
      return { instance, revision };
    });
  }

  updated(changed) {
    if ((changed.has('name') && changed.get('name') !== undefined) || (changed.has('revisionId') && changed.get('revisionId') !== undefined)) {
      this.poll.reset();
    }
  }

  async apply(revision, kind) {
    if (
      kind === 'rollback' &&
      !(await confirmAction({
        heading: `Roll back ${this.name}?`,
        message: `It goes back to revision ${revision.number}.`,
        confirmLabel: 'Roll back',
      }))
    ) {
      return;
    }
    this.busy = true;
    this.actionError = null;
    try {
      await api.apply(this.name, revision.id);
    } catch (error) {
      this.actionError = error.message;
    } finally {
      this.busy = false;
      this.poll.refresh();
    }
  }

  render() {
    const crumbs = html`<nav class="crumbs" aria-label="Breadcrumb">
      <a href="/workspaces">Workspaces</a> / <a href="/workspaces/${this.name}">${this.poll.value?.instance.displayName ?? this.name}</a> / revision
    </nav>`;
    const pending = loadState(this.poll, 'the revision');
    if (pending) {
      return html`${crumbs}${pending}`;
    }
    const { instance, revision } = this.poll.value;
    const action = revisionAction(revision, instance);
    const roles = [
      revision.id === instance.desiredRevision?.id ? 'the desired revision' : null,
      revision.id === instance.appliedRevision?.id ? 'the revision the server reports running' : null,
    ].filter(Boolean);
    return html`
      ${crumbs}
      <header class="page-head">
        <div>
          <h1>Revision ${revision.number} ${revisionStatusBadge(revision.status)}</h1>
          <p class="lead">${roles.length ? `This is ${roles.join(' and ')}.` : 'Neither desired nor applied.'}</p>
        </div>
        <div class="actions">
          <a class="button" href="/workspaces/${this.name}/edit?from=${revision.id}">Edit from here</a>
          ${action
            ? html`<button class=${action === 'apply' ? 'primary' : ''} ?disabled=${this.busy} @click=${() => this.apply(revision, action)}>
                ${ACTION_LABEL[action]}
              </button>`
            : null}
        </div>
      </header>
      ${this.actionError ? html`<div class="notice" data-tone="danger" role="alert"><strong>The action failed</strong>${this.actionError}</div>` : null}
      <section>
        <div class="card">
          <dl class="facts">
            <dt>Workspace</dt>
            <dd><a href="/workspaces/${this.name}">${instance.displayName}</a> <code class="muted">${instance.name}</code></dd>
            <dt>Author</dt>
            <dd>${revision.author}</dd>
            <dt>Created</dt>
            <dd>${when(revision.createdAt)}</dd>
            <dt>Digest</dt>
            <dd><code>${revision.digest ?? '—'}</code></dd>
          </dl>
        </div>
      </section>
      ${this.renderTeam(revision.params)}
      <section>
        <h2>Parameters</h2>
        <pre>${JSON.stringify(revision.params, null, 2)}</pre>
      </section>
      <section>
        <h2>Persona</h2>
        <h3>${OPERATOR_RULES.title} <span class="muted">at the head of AGENTS.md of every assistant</span></h3>
        ${this.renderMarkdown(revision.operatorRules, '(none)')}
        ${Object.keys(revision.personas)
          .sort()
          .map((id) => html`<h3>Assistant <code>${id}</code></h3>${this.renderPersona(revision.personas[id])}`)}
      </section>
    `;
  }

  /** The teams of the revision: each orchestrator with its specialists and what they are for. Nothing without one. */
  renderTeam(params) {
    const assistants = params?.assistants ?? {};
    const orchestrators = Object.keys(assistants).sort().filter((id) => delegatesOf(params, id).length > 0);
    if (orchestrators.length === 0) {
      return null;
    }
    const nameOf = (id) => html`${assistants[id]?.agentName ?? id} <code>${id}</code>`;
    return html`
      <section>
        <h2>Team</h2>
        ${orchestrators.map(
          (id) => html`
            <p>${nameOf(id)} delegates to:</p>
            <ul>
              ${delegatesOf(params, id).map((delegate) => html`<li>${nameOf(delegate)}${assistants[delegate]?.description ? html` — ${assistants[delegate].description}` : null}</li>`)}
            </ul>
          `,
        )}
      </section>
    `;
  }

  renderPersona(persona) {
    return PERSONA.map(
      (file) => html`
        <h3><code>${file.file}</code> <span class="muted">${file.description}</span></h3>
        ${this.renderMarkdown(persona?.[file.key], '(empty)')}
      `,
    );
  }

  renderMarkdown(source, empty) {
    return source?.trim() ? html`<div class="markdown file">${markdown(source)}</div>` : html`<p class="muted">${empty}</p>`;
  }
}

customElements.define('plane-revision-page', RevisionPage);
